import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Check, ChevronDown, Globe} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import 'flag-icons/css/flag-icons.min.css';
import {useTranslation} from '@/lib/i18n';

export default function LanguageSwitcher() {
  const {language, setLanguage, supportedLanguages} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const current = supportedLanguages.find((l) => l.code === language);

  const handleSelect = (code) => {
    if (code === language) return;

    setLanguage(code);
    const segments = location.pathname.split('/');
    segments[1] = code;
    navigate(`${segments.join('/')}${location.search}`, {replace: true});
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          data-testid="language-switcher"
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800/80 hover:border-zinc-700 text-zinc-300 hover:text-white transition text-[11px] font-mono uppercase tracking-[0.15em]"
        >
          <span className="flex items-center gap-1.5">
            {current && (
              <span
                className={`fi fi-${current.country}`}
                style={{
                  width: '22px',
                  height: '15px',
                  display: 'inline-block',
                }}
              />
            )}
            <span>{current?.code ?? language}</span>
          </span>
          <ChevronDown className="w-3 h-3 opacity-60"/>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="min-w-[160px] rounded-md border border-zinc-800 bg-[#0b0b0d] p-1 shadow-xl shadow-black/40 z-50"
        >
          {supportedLanguages.map(({code, name, country}) => (
            <DropdownMenu.Item
              key={code}
              data-testid={`lang-option-${code}`}
              onSelect={() => handleSelect(code)}
              className={`flex items-center justify-between gap-3 px-2.5 py-1.5 rounded text-[11px] font-mono tracking-[0.1em] cursor-pointer outline-none transition ${
                code === language
                  ? 'text-amber-500'
                  : 'text-zinc-300 hover:text-white hover:bg-zinc-800/80'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className={`fi fi-${country}`} style={{width: '22px', height: '15px', display: 'inline-block'}}></span>
                <span>{name}</span>
              </span>
              {code === language && <Check className="w-3.5 h-3.5"/>}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}